/**
 * Commands and key bindings of the motion columns (W2-04a; WORKPACKAGES Appendix B: `keys.*` incl.
 * `keys.addAtPlayhead`, `segments.insert|hold|jump|negate|opacity|easing`, `layers.*`, `motion.*`,
 * `columns.focus|pin|compare`; DESIGN.md §5.6, §8.2, §8.3).
 *
 * Every command has a menu ("motion.context", the column header menu) or palette path; the frequent ones have keys
 * in both presets: K / Shift+K, Enter / C, H, J, A, S, arrows, Delete. A and S are polymorphic by SELECTION KIND
 * (keys here, notes in the note lane): their bindings require keyframes or segments and no notes, the note lane's
 * require notes (mixed selections are the note lane's). No key changes meaning with the hover position, except the
 * RPE preset's R…R / Q…R placement "over a motion column" (DESIGN.md §8.3). No reserved, Ctrl+Alt or Alt chords are
 * bound, so no Alt-free or Mac alternates are needed.
 */

import { Core } from '../../core/commands/index';
import { clampToDomain } from '../../core/commands/keyValueRules';
import { deleteSegments, planSegmentDelete } from '../../core/commands/segmentDelete';
import type { Ease } from '../../core/ease/types';
import { hasGifTexture } from '../../core/model/lineKind';
import { absentMessageKey } from '../../core/model/props';
import type { KeyId, LineId, LineProp, TrackValue } from '../../core/model/types';
import { Beats } from '../../core/time/beat';
import type { Json, MessageRef } from '../../core/util/types';
import { DIALOG } from '../../app/dialogs/types';
import type {
  AppServices,
  CommandContext,
  CommandDef,
  KeyBinding,
  KeymapPresetId,
  MenuPlacement,
} from '../../app/registry/types';
import { asTimelineHostController } from '../../render/timeline/TimelineHost';
import type { ColumnRef, DocStore, Selection } from '../../state/types';
import { clearPinned, compareCandidates, compareLines, isPinned, togglePin } from './CompareLines';
import { columnIdOf, findLane, isLayered, layersOf, lineColumns, sameRef } from './columnSpec';
import { laneKeyOf, laneTrack, trackKeys } from './columnScene';
import { neutralPropName, propNameAcross, propNames } from './format';
import { fineTune, nudgeStepOf, niceStep, roundTo } from './motionGestures';
import {
  addKeys,
  animatedProps,
  confirmGuidedKeys,
  confirmGuidedRefs,
  insertAnimatedKeys,
  levelOf,
  lineTypeChange,
  openEasingPicker,
  propCtxOf,
  switchToFull,
  tailOf,
} from './motionActions';
import { m } from './motion.messages';
import { cancelPlacement, placementDigit, placementPress } from './rpeSegmentPlacement';
import { motionSession, type MotionSession } from './session';
import {
  keysOrSegments,
  newTrackValue,
  ref,
  runTx,
  segmentsOrKeys,
  selectedKeys,
  selectedSegments,
  snapDiv,
  snappedPlayhead,
  type Services,
} from './txn';

type Enabled = true | MessageRef;

// ---------------------------------------------------------------------------------------------------------------
// Argument helpers
// ---------------------------------------------------------------------------------------------------------------

function obj(args: unknown): Readonly<Record<string, unknown>> {
  return typeof args === 'object' && args !== null && !Array.isArray(args) ? (args as Record<string, unknown>) : {};
}

function numArg(args: unknown, key: string): number | undefined {
  if (typeof args === 'number' && Number.isFinite(args)) return args;
  const v = obj(args)[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

function strArg(args: unknown, key: string): string | undefined {
  if (typeof args === 'string') return args;
  const v = obj(args)[key];
  return typeof v === 'string' ? v : undefined;
}

const PROPS: readonly LineProp[] = [
  'moveX',
  'moveY',
  'rotate',
  'alpha',
  'speed',
  'scaleX',
  'scaleY',
  'color',
  'text',
  'incline',
  'paint',
  'gif',
];

function propArg(args: unknown): LineProp | undefined {
  const p = strArg(args, 'prop');
  return p !== undefined && (PROPS as readonly string[]).includes(p) ? (p as LineProp) : undefined;
}

/** A ColumnRef from args ({line, prop, layer}), else the focused column. */
function columnArg(ctx: Pick<Services, 'editor' | 'settings'>, args: unknown): ColumnRef | null {
  const prop = propArg(args);
  const line = numArg(args, 'line');
  const layer = numArg(args, 'layer');
  const focus = ctx.editor.getState().focusedColumn;
  if (prop !== undefined) {
    const l = (line ?? focus?.line ?? ctx.editor.getState().activeLine) as LineId | null | undefined;
    if (l === null || l === undefined) return null;
    // Without an explicit layer: the edited layer in Full, the base layer (the Σ column edits it) in Guided.
    const fallback = levelOf(ctx) === 'full' ? ctx.editor.getState().activeLayer : 0;
    return { line: l, prop, layer: isLayered(prop) ? Math.max(0, Math.floor(layer ?? fallback)) : 0 };
  }
  return focus;
}

const session = (ctx: CommandContext): MotionSession => motionSession(ctx);

function projectOpen(ctx: CommandContext): Enabled {
  return ctx.store !== null ? true : ref(m.needProject);
}

function hasKeys(ctx: CommandContext): Enabled {
  const store = ctx.store;
  if (store === null) return ref(m.needProject);
  const sel = ctx.editor.getState().selection;
  return keysOrSegments(store, sel).length > 0 ? true : ref(m.needKeys);
}

function hasSegments(ctx: CommandContext): Enabled {
  const store = ctx.store;
  if (store === null) return ref(m.needProject);
  return segmentsOrKeys(store, ctx.editor.getState().selection).length > 0 ? true : ref(m.needSegments);
}

function hasFocus(ctx: CommandContext): Enabled {
  if (ctx.store === null) return ref(m.needProject);
  return ctx.editor.getState().focusedColumn !== null ? true : ref(m.needFocus);
}

/**
 * Commands that take an explicit column ({ line, prop, layer }) from menus, chips or other packages: enabled with a
 * project; without args they act on the focused column, and `run` explains when there is none. A focused column of
 * an extended property cannot have layers.
 */
function layeredFocus(ctx: CommandContext): Enabled {
  if (ctx.store === null) return ref(m.needProject);
  const col = ctx.editor.getState().focusedColumn;
  return col === null || isLayered(col.prop) ? true : ref(m.needLayered);
}

/** The column of args or the focused one; toasts "Click a motion column first" when there is none. */
function targetColumn(ctx: CommandContext, args: unknown, layered = false): ColumnRef | null {
  const col = columnArg(ctx, args);
  if (col === null) {
    ctx.ui.toast(ref(m.needFocus), { kind: 'warning' });
    return null;
  }
  if (layered && !isLayered(col.prop)) {
    ctx.ui.toast(ref(m.needLayered), { kind: 'warning' });
    return null;
  }
  return col;
}

const ctxMenu = (group: string, order: number): MenuPlacement => ({ menu: 'motion.context', group, order });

function propLabel(ctx: Services, col: ColumnRef): string {
  const store = ctx.store;
  const pc = store === null ? { kind: 'normal' as const, uiMover: false } : propCtxOf(store, col.line);
  return ctx.i18n.t(propNames(col.prop, pc).full);
}

// ---------------------------------------------------------------------------------------------------------------
// Keys
// ---------------------------------------------------------------------------------------------------------------

/** K: a key at the (snapped) playhead on the focused column; on every selected line when several are selected. */
export async function addKeyAtPlayhead(ctx: CommandContext): Promise<KeyId[]> {
  const store = ctx.store;
  if (store === null) return [];
  const st = ctx.editor.getState();
  const col = st.focusedColumn;
  if (col === null) {
    ctx.ui.toast(ref(m.needFocus), { kind: 'warning' });
    return [];
  }
  const s = session(ctx);
  // The focused column, the same property of every selected line, and of every line whose keys or segments of that
  // property are selected (Compare lines: "K applies to all selected columns of all lines").
  const lines = new Set<LineId>([col.line]);
  if (st.selection.lines.size > 1) for (const l of st.selection.lines) lines.add(l);
  for (const id of [...st.selection.keys, ...st.selection.segments]) {
    const k = store.doc.keys.get(id);
    const owner = k === undefined ? undefined : store.doc.tracks.get(k.track)?.owner;
    if (owner?.kind === 'line' && owner.prop === col.prop && owner.layer === col.layer) lines.add(owner.line);
  }
  const targets = store.doc.lineOrder
    .filter((l) => lines.has(l))
    .map((line): ColumnRef => ({ line, prop: col.prop, layer: col.layer }));
  if (!(await confirmGuidedRefs(ctx, s, targets))) return [];
  const t = snappedPlayhead(ctx);
  return addKeys(ctx, s, targets, t);
}

/**
 * Shift+K: a key at the playhead on every animated property of the active line (of every selected line); keys after
 * the end of a track give their new segment the last used curve.
 */
export async function addKeysAll(ctx: CommandContext): Promise<KeyId[]> {
  const store = ctx.store;
  if (store === null) return [];
  const st = ctx.editor.getState();
  const lines =
    st.selection.lines.size > 1
      ? store.doc.lineOrder.filter((l) => st.selection.lines.has(l))
      : st.activeLine === null
        ? []
        : [st.activeLine];
  if (lines.length === 0) {
    ctx.ui.toast(ref(m.needActiveLine), { kind: 'warning' });
    return [];
  }
  const layer = levelOf(ctx) === 'full' ? st.activeLayer : 0;
  const s = session(ctx);
  const refs = lines.flatMap((line) => animatedProps(store, line, layer).map((prop) => ({ line, prop, layer })));
  if (!(await confirmGuidedRefs(ctx, s, refs))) return [];
  const t = snappedPlayhead(ctx);
  const tail = tailOf(ctx, s);
  const out = runTx(ctx, { label: ref(m.txAddKeys), command: 'keys.addAll', focus: { beat: t } }, (tx) => {
    const keys = insertAnimatedKeys(tx, lines, layer, t, tail);
    if (keys.length === 0) tx.fail(ref('cmdMotion.nothingAnimated'));
    tx.setSelection({ keys, segments: [], notes: [] });
    return keys;
  });
  if (!out.ok) return [];
  s.touch();
  return out.value;
}

/** "Add keyframe here" (context menu): at the pointer's beat on the lane under it. */
function addKeyHere(ctx: CommandContext): void {
  const s = session(ctx);
  const lp = s.lastPointer;
  const spec = lp === null ? undefined : s.specs.find((c) => c.id === lp.instanceId);
  const lane = spec?.lanes[Math.max(0, lp?.lane ?? 0)];
  if (lp === null || spec === undefined || lane === undefined) {
    void addKeyAtPlayhead(ctx);
    return;
  }
  const target: ColumnRef = { line: spec.line, prop: lane.prop, layer: spec.sumView ? 0 : lane.layer };
  const snapped = snapBeatOf(ctx, lp.beat);
  void (async () => {
    if (!(await confirmGuidedRefs(ctx, s, [target]))) return;
    addKeys(ctx, s, [target], snapped);
  })();
}

function snapBeatOf(ctx: CommandContext, f: number): ReturnType<typeof Beats.make> {
  const host = ctx.timeline;
  if (host !== null) return host.axis.snap(Math.max(0, f));
  const div = snapDiv(ctx);
  return Beats.snap(Beats.fromF64(Math.max(0, f), 1920), div);
}

async function deleteSelectedKeys(ctx: CommandContext): Promise<void> {
  const store = ctx.store;
  if (store === null) return;
  const keys = selectedKeys(store, ctx.editor.getState().selection);
  if (keys.length === 0) {
    ctx.ui.toast(ref(m.needKeys), { kind: 'warning' });
    return;
  }
  if (!(await confirmGuidedKeys(ctx, session(ctx), keys))) return;
  const res = store.transact({ label: ref(m.txDeleteKeys, { count: keys.length }), command: 'keys.delete' }, (tx) => {
    Core.keys.deleteKeys(tx, { keys });
    tx.setSelection({ keys: [], segments: [] });
  });
  if (!res.ok) {
    const first = keys
      .map((id) => store.doc.keys.get(id))
      .find((k) => k !== undefined && store.idx.prevKey(k.id) === undefined);
    const tr = first === undefined ? undefined : store.doc.tracks.get(first.track);
    const owner = tr?.owner;
    ctx.ui.toast(res.reason, {
      kind: 'error',
      ...(owner?.kind === 'line'
        ? {
            action: {
              label: ref(m.cmdResetProperty),
              command: 'keys.resetProperty',
              args: { line: owner.line, prop: owner.prop, layer: owner.layer },
            },
          }
        : {}),
    });
    return;
  }
  for (const n of res.notes) ctx.ui.toast(n, { kind: 'info' });
}

/** Selected segments (their start keys) that are not also selected as keys. */
function selectedSegmentStarts(store: DocStore, sel: Selection): KeyId[] {
  return [...sel.segments].filter((id) => !sel.keys.has(id) && store.doc.keys.has(id));
}

/**
 * Delete / Backspace on selected segments (UX-037, RPE EVT-07): a middle segment becomes a Stay, the last segment of
 * a track loses its end key (core/commands/segmentDelete.ts). Segments that already stay say so and offer to select
 * their keyframes.
 */
async function deleteSelectedSegments(ctx: CommandContext): Promise<void> {
  const store = ctx.store;
  if (store === null) return;
  const segments = selectedSegmentStarts(store, ctx.editor.getState().selection);
  if (segments.length === 0) {
    ctx.ui.toast(ref(m.needSegments), { kind: 'warning' });
    return;
  }
  const plan = planSegmentDelete(store, segments);
  if (plan.stay.length + plan.trimEnds.length === 0) {
    ctx.ui.toast(ref(m.toastSegmentsAlreadyStay, { count: plan.unchanged.length }), {
      kind: 'info',
      action: { label: ref(m.cmdSelectSegmentKeys), command: 'segments.selectKeys' },
    });
    return;
  }
  if (!(await confirmGuidedKeys(ctx, session(ctx), segments))) return;
  const count = plan.stay.length + plan.trimEnds.length;
  const res = store.transact({ label: ref(m.txDeleteSegments, { count }), command: 'segments.delete' }, (tx) => {
    const r = deleteSegments(tx, { segments });
    tx.setSelection({ keys: [], segments: plan.stay.filter((id) => tx.doc.keys.has(id)) });
    return r;
  });
  if (!res.ok) {
    ctx.ui.toast(res.reason, { kind: 'error' });
    return;
  }
  ctx.ui.toast(ref(m.toastSegmentsDeleted, { count, trimmed: res.value.trimmed }), {
    kind: 'info',
    undo: true,
    durationMs: 4000,
  });
  for (const n of res.notes) ctx.ui.toast(n, { kind: 'info' });
}

/** Selects the start and end keys of the selected segments (the "Select its keyframes" toast action). */
function selectSegmentKeys(ctx: CommandContext): void {
  const store = ctx.store;
  if (store === null) return;
  const sel = ctx.editor.getState().selection;
  const keys = new Set<KeyId>(sel.keys);
  for (const id of sel.segments) {
    if (!store.doc.keys.has(id)) continue;
    keys.add(id);
    const end = store.idx.nextKey(id);
    if (end !== undefined) keys.add(end);
  }
  if (keys.size === 0) {
    ctx.ui.toast(ref(m.needSegments), { kind: 'warning' });
    return;
  }
  const first = [...keys][0] as KeyId;
  ctx.editor.getState().select({ keys, segments: new Set(), primary: { kind: 'key', id: first } }, 'replace');
}

async function nudgeValues(ctx: CommandContext, dir: 1 | -1, times: number): Promise<void> {
  const store = ctx.store;
  if (store === null) return;
  const s = session(ctx);
  if (!(await confirmGuidedKeys(ctx, s, keysOrSegments(store, ctx.editor.getState().selection)))) return;
  const sel = ctx.editor.getState().selection;
  const keys = keysOrSegments(store, sel);
  const edits: Array<{ key: KeyId; part: 'in' | 'out' | 'both'; value: number }> = [];
  for (const id of keys) {
    const k = store.doc.keys.get(id);
    if (k === undefined || typeof k.vIn !== 'number' || typeof k.vOut !== 'number') continue;
    const step = nudgeStepOf(s, id) * times * dir;
    const jump = store.idx.prevKey(id) !== undefined && k.vIn !== k.vOut;
    const tr = store.doc.tracks.get(k.track);
    // Nudges stay inside the property's domain (UX-042).
    const moved = (v: number): number =>
      roundTo(tr?.owner.kind === 'line' ? clampToDomain(tr.owner.prop, v, v + step) : v + step, 1e-6);
    if (jump) {
      edits.push({ key: id, part: 'out', value: moved(k.vOut) });
      const prev = store.idx.prevKey(id);
      const pk = prev === undefined ? undefined : store.doc.keys.get(prev);
      if (pk?.ease.kind !== 'hold') edits.push({ key: id, part: 'in', value: moved(k.vIn) });
    } else edits.push({ key: id, part: 'both', value: moved(k.vOut) });
  }
  if (edits.length === 0) {
    ctx.ui.toast(ref(m.needNumeric), { kind: 'warning' });
    return;
  }
  const selKey = keys
    .slice()
    .sort((a, b) => a - b)
    .join(',');
  runTx(
    ctx,
    { label: ref(m.txNudge), command: dir > 0 ? 'keys.valueUp' : 'keys.valueDown', coalesceKey: `nudge:${selKey}` },
    (tx) => {
      for (const e of edits) Core.keys.setKeyValues(tx, { keys: [e.key], part: e.part, value: e.value });
    },
  );
}

async function nudgeTime(ctx: CommandContext, dir: 1 | -1, times: number): Promise<void> {
  const store = ctx.store;
  if (store === null) return;
  if (!(await confirmGuidedKeys(ctx, session(ctx), keysOrSegments(store, ctx.editor.getState().selection)))) return;
  const keys = keysOrSegments(store, ctx.editor.getState().selection);
  if (keys.length === 0) return;
  const div = snapDiv(ctx);
  const dt = Beats.make(0, times * dir, div);
  const selKey = keys
    .slice()
    .sort((a, b) => a - b)
    .join(',');
  runTx(
    ctx,
    { label: ref(m.txNudge), command: dir > 0 ? 'keys.later' : 'keys.earlier', coalesceKey: `nudge:${selKey}` },
    (tx) => {
      Core.keys.moveKeys(tx, { keys, dt });
    },
  );
}

function selectColumn(ctx: CommandContext, args: unknown): void {
  const store = ctx.store;
  const col = columnArg(ctx, args);
  if (store === null || col === null) return;
  const ids = trackKeys(store, laneTrack(store, col)).map((k) => k.id);
  ctx.editor
    .getState()
    .select(
      { keys: new Set(ids), ...(ids[0] === undefined ? {} : { primary: { kind: 'key' as const, id: ids[0] } }) },
      'replace',
    );
}

export async function resetProperty(ctx: AppServices, args: unknown): Promise<void> {
  const store = ctx.store;
  const col = columnArg(ctx, args);
  if (store === null || col === null) return;
  if (!(await confirmGuidedRefs(ctx, motionSession(ctx), [col]))) return;
  const label = propLabel(ctx, col);
  const pc = propCtxOf(store, col.line);
  const out = runTx(
    ctx,
    { label: ref(m.txReset, { prop: label }), command: 'keys.resetProperty', focus: { line: col.line } },
    (tx) => {
      Core.keys.resetProperty(tx, { line: col.line, prop: col.prop, layer: col.layer });
    },
  );
  if (!out.ok) return;
  ctx.ui.toast(ref(m.resetDone, { prop: label, absent: ctx.i18n.t(absentMessageKey(col.prop, pc.kind, pc.uiMover)) }), {
    kind: 'info',
    undo: true,
  });
}

/** Split at the playhead: a key at the playhead in every selected segment that contains it (else the focused column). */
async function splitAtPlayhead(ctx: CommandContext): Promise<void> {
  const store = ctx.store;
  if (store === null) return;
  const t = snappedPlayhead(ctx);
  const segs = selectedSegments(store, ctx.editor.getState().selection);
  const targets: ColumnRef[] = [];
  for (const id of segs) {
    const k = store.doc.keys.get(id);
    const next = store.idx.nextKey(id);
    const nk = next === undefined ? undefined : store.doc.keys.get(next);
    const tr = k === undefined ? undefined : store.doc.tracks.get(k.track);
    if (k === undefined || nk === undefined || tr?.owner.kind !== 'line') continue;
    if (!(Beats.lt(k.t, t) && Beats.lt(t, nk.t))) continue;
    const ref0: ColumnRef = { line: tr.owner.line, prop: tr.owner.prop, layer: tr.owner.layer };
    if (!targets.some((x) => sameRef(x, ref0))) targets.push(ref0);
  }
  if (targets.length === 0) {
    await addKeyAtPlayhead(ctx);
    return;
  }
  const s = session(ctx);
  if (!(await confirmGuidedRefs(ctx, s, targets))) return;
  addKeys(ctx, s, targets, t, m.txSplit);
}

// ---------------------------------------------------------------------------------------------------------------
// Segments
// ---------------------------------------------------------------------------------------------------------------

async function toggleHold(ctx: CommandContext): Promise<void> {
  const store = ctx.store;
  if (store === null) return;
  const segments = segmentsOrKeys(store, ctx.editor.getState().selection);
  if (segments.length === 0) {
    ctx.ui.toast(ref(m.needSegments), { kind: 'warning' });
    return;
  }
  if (!(await confirmGuidedKeys(ctx, session(ctx), segments))) return;
  runTx(ctx, { label: ref(m.txHold), command: 'segments.hold' }, (tx) => {
    Core.keys.toggleHold(tx, { segments });
  });
}

/** Keys named by a command argument `{ keys: [...] }` (toast actions), or null without one. */
function keysArg(store: DocStore, args: unknown): KeyId[] | null {
  if (typeof args !== 'object' || args === null || !('keys' in args)) return null;
  const list = (args as { readonly keys: unknown }).keys;
  if (!Array.isArray(list)) return null;
  return list.filter((id): id is KeyId => typeof id === 'number' && store.doc.keys.has(id as KeyId));
}

/**
 * J: remove the jumps of selected jump keys (Continue from previous, front to back), else make jumps. With
 * `{ keys }` (the "Made a jump" toast's action) it only removes the jumps of those keys.
 */
async function toggleJump(ctx: CommandContext, args?: unknown): Promise<void> {
  const store = ctx.store;
  if (store === null) return;
  const sel = ctx.editor.getState().selection;
  const named = keysArg(store, args);
  const keys =
    named ??
    (selectedKeys(store, sel).length > 0
      ? selectedKeys(store, sel)
      : keysOrSegments(store, sel).filter((id) => store.idx.prevKey(id) !== undefined));
  if (keys.length === 0) {
    ctx.ui.toast(ref(m.needKeys), { kind: 'warning' });
    return;
  }
  if (!(await confirmGuidedKeys(ctx, session(ctx), keys))) return;
  const isJump = (id: KeyId): boolean => {
    const k = store.doc.keys.get(id);
    return k !== undefined && store.idx.prevKey(id) !== undefined && !sameValueOf(k.vIn, k.vOut);
  };
  if (keys.some(isJump)) {
    runTx(ctx, { label: ref(m.txContinue), command: 'segments.jump' }, (tx) => {
      Core.keys.removeJump(tx, { keys: keys.filter(isJump), keep: 'in' });
    });
    return;
  }
  if (named !== null) return;
  const s = session(ctx);
  const jumps: Array<{ key: KeyId; vIn: TrackValue; vOut: TrackValue }> = [];
  for (const id of keys) {
    const k = store.doc.keys.get(id);
    const tr = k === undefined ? undefined : store.doc.tracks.get(k.track);
    if (
      k === undefined ||
      tr?.owner.kind !== 'line' ||
      store.idx.prevKey(id) === undefined ||
      typeof k.vIn !== 'number'
    )
      continue;
    const range = s.lastRanges.get(laneKeyOf(tr.owner.line, tr.owner.prop, tr.owner.layer));
    const span = range === undefined ? nudgeStepOf(s, id) * 40 : range.hi - range.lo;
    const offset = niceStep(Math.abs(span) / 8);
    jumps.push({ key: id, vIn: k.vIn, vOut: roundTo(k.vIn + offset, offset / 10) });
  }
  if (jumps.length === 0) {
    ctx.ui.toast(ref(m.needNumeric), { kind: 'warning' });
    return;
  }
  runTx(ctx, { label: ref(m.txMakeJump), command: 'segments.jump' }, (tx) => {
    for (const j of jumps) Core.keys.makeJump(tx, j);
  });
}

function sameValueOf(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((x, i) => x === b[i]);
  return a === b;
}

async function negate(ctx: CommandContext): Promise<void> {
  const store = ctx.store;
  if (store === null) return;
  const keys = keysOrSegments(store, ctx.editor.getState().selection);
  if (!(await confirmGuidedKeys(ctx, session(ctx), keys))) return;
  runTx(ctx, { label: ref(m.txNegate), command: 'segments.negate' }, (tx) => {
    Core.transform.negateKeys(tx, { keys });
  });
}

function opacityKeys(store: DocStore, keys: readonly KeyId[]): KeyId[] {
  return keys.filter((id) => {
    const k = store.doc.keys.get(id);
    const tr = k === undefined ? undefined : store.doc.tracks.get(k.track);
    return tr?.owner.kind === 'line' && tr.owner.prop === 'alpha';
  });
}

async function opacityFull(ctx: CommandContext): Promise<void> {
  const store = ctx.store;
  if (store === null) return;
  const keys = opacityKeys(store, keysOrSegments(store, ctx.editor.getState().selection));
  if (keys.length === 0) {
    ctx.ui.toast(ref(m.needOpacityKeys), { kind: 'warning' });
    return;
  }
  if (!(await confirmGuidedKeys(ctx, session(ctx), keys))) return;
  runTx(ctx, { label: ref(m.txOpacityFull), command: 'segments.opacity' }, (tx) => {
    Core.transform.opacityFull(tx, { keys });
  });
}

// ---------------------------------------------------------------------------------------------------------------
// Layers
// ---------------------------------------------------------------------------------------------------------------

export function muteKeyOf(prop: LineProp, layer: number): string {
  return `${prop}:${String(layer)}`;
}

export function isMuted(ctx: Pick<Services, 'editor'>, line: LineId, prop: LineProp, layer: number): boolean {
  return ctx.editor.getState().filters.mutedLayers.get(line)?.has(muteKeyOf(prop, layer)) === true;
}

/** Layers of (line, prop) that have keys plus the active one. */
export function layerList(store: DocStore, line: LineId, prop: LineProp, extra: readonly number[] = []): number[] {
  return [...new Set([0, ...layersOf(store.doc, store.idx, line, prop), ...extra])].sort((a, b) => a - b);
}

function setMuted(
  ctx: Services,
  line: LineId,
  prop: LineProp,
  mute: ReadonlySet<number>,
  all: readonly number[],
): void {
  const filters = ctx.editor.getState().filters;
  const map = new Map(filters.mutedLayers);
  const set = new Set([...(map.get(line) ?? [])].filter((k) => !all.some((l) => k === muteKeyOf(prop, l))));
  for (const l of mute) set.add(muteKeyOf(prop, l));
  if (set.size === 0) map.delete(line);
  else map.set(line, set);
  ctx.editor.getState().setFilters({ mutedLayers: map });
}

/** Mute / unmute a layer in the preview (never exported; the "Preview differs from export" banner shows). */
export function toggleMute(ctx: Services, col: ColumnRef): boolean {
  const store = ctx.store;
  if (store === null || !isLayered(col.prop)) return false;
  const all = layerList(store, col.line, col.prop, [col.layer]);
  const muted = new Set(all.filter((l) => isMuted(ctx, col.line, col.prop, l)));
  const next = !muted.has(col.layer);
  if (next) muted.add(col.layer);
  else muted.delete(col.layer);
  setMuted(ctx, col.line, col.prop, muted, all);
  if (next) ctx.ui.toast(ref(m.layerMutedToast, { n: col.layer, prop: propLabel(ctx, col) }), { kind: 'info' });
  return next;
}

/** A layer is soloed when every other layer of the property is muted and it is not. */
export function isSolo(ctx: Services, col: ColumnRef): boolean {
  const store = ctx.store;
  if (store === null) return false;
  const all = layerList(store, col.line, col.prop, [col.layer]);
  if (all.length < 2 || isMuted(ctx, col.line, col.prop, col.layer)) return false;
  return all.every((l) => l === col.layer || isMuted(ctx, col.line, col.prop, l));
}

export function toggleSolo(ctx: Services, col: ColumnRef): boolean {
  const store = ctx.store;
  if (store === null || !isLayered(col.prop)) return false;
  const all = layerList(store, col.line, col.prop, [col.layer]);
  if (isSolo(ctx, col)) {
    setMuted(ctx, col.line, col.prop, new Set(), all);
    return false;
  }
  setMuted(ctx, col.line, col.prop, new Set(all.filter((l) => l !== col.layer)), all);
  ctx.ui.toast(ref(m.layerSoloToast, { n: col.layer, prop: propLabel(ctx, col) }), { kind: 'info' });
  return true;
}

export function unmuteAll(ctx: Pick<Services, 'editor' | 'ui'>): void {
  const st = ctx.editor.getState();
  if (st.filters.mutedLayers.size === 0) return;
  st.setFilters({ mutedLayers: new Map() });
  ctx.ui.toast(ref(m.layerUnmutedAll), { kind: 'info' });
}

/** Switch the edited layer (Full); the focused column follows when it is a summed property of the same line. */
export function setActiveLayer(ctx: Pick<Services, 'editor'>, layer: number): void {
  const st = ctx.editor.getState();
  const l = Math.max(0, Math.floor(layer));
  st.setActiveLayer(l);
  const f = ctx.editor.getState().focusedColumn;
  if (f !== null && isLayered(f.prop) && f.layer !== l) ctx.editor.getState().setFocusedColumn({ ...f, layer: l });
}

export function addLayer(ctx: Services, line: LineId, prop: LineProp): number | null {
  const out = runTx(ctx, { label: ref(m.txLayerAdd), command: 'layers.add', focus: { line } }, (tx) =>
    Core.keys.addLayer(tx, { line, prop }),
  );
  if (!out.ok) return null;
  setActiveLayer(ctx, out.value);
  ctx.editor.getState().setFocusedColumn({ line, prop, layer: out.value });
  return out.value;
}

export async function deleteLayer(ctx: Services, col: ColumnRef): Promise<boolean> {
  const store = ctx.store;
  if (store === null) return false;
  const track = laneTrack(store, col);
  const count = track === undefined ? 0 : store.idx.keysByTrack(track).length;
  if (count > 1) {
    const ok = await ctx.ui.confirm({
      title: ref(m.layerDeleteConfirmTitle, { n: col.layer, prop: propLabel(ctx, col) }),
      body: ref(m.layerDeleteConfirmBody, { count }),
      confirm: ref(m.layerDeleteConfirm),
      danger: true,
    });
    if (!ok) return false;
  }
  const out = runTx(ctx, { label: ref(m.txLayerDelete), command: 'layers.delete', focus: { line: col.line } }, (tx) => {
    Core.keys.deleteLayer(tx, { line: col.line, prop: col.prop, layer: col.layer });
  });
  if (!out.ok) return false;
  // Forget its preview mute and stop editing it.
  if (isMuted(ctx, col.line, col.prop, col.layer)) toggleMute(ctx, col);
  if (ctx.editor.getState().activeLayer === col.layer) setActiveLayer(ctx, 0);
  return true;
}

export function renameLayer(ctx: Services, line: LineId, layer: number, name: string): boolean {
  const store = ctx.store;
  const rec = store?.doc.lines.get(line);
  if (store === null || rec === undefined) return false;
  const names = [...rec.editor.layerNames];
  while (names.length <= layer) names.push(null);
  const trimmed = name.trim();
  names[layer] = trimmed === '' ? null : trimmed;
  while (names.length > 0 && names[names.length - 1] === null) names.pop();
  return runTx(ctx, { label: ref(m.txLayerRename), command: 'layers.rename' }, (tx) => {
    Core.lines.patchLineEditor(tx, { id: line, patch: { layerNames: names } });
  }).ok;
}

export function moveSelectionToLayer(ctx: Services, layer: number): boolean {
  const store = ctx.store;
  if (store === null) return false;
  const segments = segmentsOrKeys(store, ctx.editor.getState().selection);
  if (segments.length === 0) {
    ctx.ui.toast(ref(m.needSegments), { kind: 'warning' });
    return false;
  }
  return runTx(ctx, { label: ref(m.txLayerMove, { n: layer }), command: 'layers.moveSelection' }, (tx) => {
    Core.keys.moveSegmentsToLayer(tx, { segments, layer });
  }).ok;
}

// ---------------------------------------------------------------------------------------------------------------
// Motion (column options, properties, curves)
// ---------------------------------------------------------------------------------------------------------------

export function toggleLink(ctx: Services, line: LineId): boolean {
  const store = ctx.store;
  const rec = store?.doc.lines.get(line);
  if (store === null || rec === undefined) return false;
  const next = !rec.editor.xyLinked;
  const out = runTx(ctx, { label: ref(next ? m.txLink : m.txUnlink), command: 'motion.toggleLink' }, (tx) => {
    Core.lines.patchLineEditor(tx, { id: line, patch: { xyLinked: next } });
  });
  if (out.ok)
    ctx.ui.toast(ref(next ? m.linkedOn : m.linkedOff, { line: rec.name }), { kind: 'info', durationMs: 2500 });
  return out.ok ? next : rec.editor.xyLinked;
}

/** Properties "Add property" offers on a line (DESIGN.md §2.4): Guided Colour and Size/Length, Full the rest. */
export function addablePropsOf(store: DocStore, line: LineId, level: 'guided' | 'full'): LineProp[] {
  const pc = propCtxOf(store, line);
  const rec = store.doc.lines.get(line);
  const gifTexture = rec !== undefined && hasGifTexture(store.doc, rec);
  const has = (p: LineProp): boolean => (store.idx.tracksOfLine(line).get(p)?.length ?? 0) > 0;
  const list: LineProp[] =
    level === 'guided' ? ['color', 'scaleX'] : ['scaleX', 'scaleY', 'color', 'text', 'incline', 'paint', 'gif'];
  return list.filter((p) => {
    if (has(p)) return false;
    if (p === 'text' || p === 'paint') return pc.kind === 'normal' && !pc.uiMover;
    if (p === 'gif') return gifTexture;
    if (p === 'incline') return pc.kind === 'normal' && !pc.uiMover;
    return true;
  });
}

/** Add property: a track at beat 0 holding the value that renders like "not set" (text / brush / GIF change the line type). */
export async function addProperty(ctx: Services, line: LineId, prop: LineProp): Promise<boolean> {
  const store = ctx.store;
  if (store === null || !store.doc.lines.has(line)) return false;
  const pc = propCtxOf(store, line);
  // Text and brush make a different kind of line, GIF progress needs a GIF image: the line type dialog explains it.
  const change = lineTypeChange(store, line, prop);
  if (change !== null) {
    const res = await ctx.ui.openDialog(DIALOG.lineType, { line, target: change });
    return res !== null;
  }
  const label = ctx.i18n.t(propNames(prop, pc).full);
  const out = runTx(
    ctx,
    { label: ref(m.txAddProperty, { prop: label }), command: 'motion.addProperty', focus: { line } },
    (tx) => {
      if (prop === 'incline') {
        Core.lines.setIncline(tx, { line, degrees: 0 });
        return;
      }
      Core.keys.ensureTrack(tx, {
        owner: { kind: 'line', line, prop, layer: 0 },
        value: newTrackValue(prop, 0, pc),
        at: Beats.ZERO,
      });
    },
  );
  if (!out.ok) return false;
  const st = ctx.editor.getState();
  st.setFocusedColumn({ line, prop, layer: 0 });
  if (!st.timeline.showMotionColumns) st.setTimeline({ showMotionColumns: true });
  return true;
}

function copyCurve(ctx: CommandContext): void {
  const store = ctx.store;
  if (store === null) return;
  const segs = segmentsOrKeys(store, ctx.editor.getState().selection);
  const first = segs[0];
  const e = first === undefined ? undefined : store.doc.keys.get(first)?.ease;
  if (e === undefined) {
    ctx.ui.toast(ref(m.needSegments), { kind: 'warning' });
    return;
  }
  session(ctx).copiedCurve = e;
  ctx.ui.toast(ref(m.curveCopied), { kind: 'info', durationMs: 2000 });
}

async function pasteCurve(ctx: CommandContext): Promise<void> {
  const store = ctx.store;
  const s = session(ctx);
  const curve: Ease | null = s.copiedCurve;
  if (store === null) return;
  if (curve === null) {
    ctx.ui.toast(ref(m.needCurve), { kind: 'warning' });
    return;
  }
  const segments = segmentsOrKeys(store, ctx.editor.getState().selection);
  if (segments.length === 0) {
    ctx.ui.toast(ref(m.needSegments), { kind: 'warning' });
    return;
  }
  if (!(await confirmGuidedKeys(ctx, s, segments))) return;
  const out = runTx(ctx, { label: ref(m.txPasteCurve), command: 'motion.pasteCurve' }, (tx) =>
    Core.keys.setEase(tx, { segments, ease: curve }),
  );
  if (out.ok) {
    s.lastCurve = curve;
    ctx.ui.toast(ref(m.curvePasted, { count: segments.length - out.value.skipped }), {
      kind: 'info',
      durationMs: 2000,
    });
  }
}

/** The RPE JSON of the events the exporter writes for a lane (Raw events, "Copy as RPE JSON"). */
export function rawEventsJson(ctx: Services, col: ColumnRef): string | null {
  const engine = ctx.engine;
  if (engine === null) return null;
  try {
    const ir = engine.irLine(col.line);
    const key = `${col.prop}Events`;
    let list: unknown;
    if (isLayered(col.prop)) list = (ir.eventLayers[col.layer] as Record<string, unknown> | null | undefined)?.[key];
    else if (ir.extended !== null && ir.extended !== 'absent')
      list = (ir.extended as unknown as Record<string, unknown>)[key];
    if (!Array.isArray(list)) return null;
    const events = (list as ReadonlyArray<Record<string, unknown>>).map((e) => {
      const out: Record<string, Json> = {};
      for (const [k, v] of Object.entries(e)) {
        if (k === 'origin' || k === 'srcKey' || k === 'extra') continue;
        out[k] = v as Json;
      }
      for (const pair of (e.extra as ReadonlyArray<readonly [string, Json]> | undefined) ?? []) out[pair[0]] = pair[1];
      return out;
    });
    return JSON.stringify(events, null, 2);
  } catch {
    return null;
  }
}

async function copyRaw(ctx: CommandContext, args: unknown): Promise<void> {
  const col = columnArg(ctx, args);
  if (col === null) return;
  const text = rawEventsJson(ctx, col);
  if (text === null) {
    ctx.ui.toast(ref(m.rawCopyFailed), { kind: 'error' });
    return;
  }
  try {
    await navigator.clipboard.writeText(text);
    ctx.ui.toast(ref(m.rawCopied), { kind: 'success', durationMs: 2000 });
  } catch {
    ctx.ui.toast(ref(m.rawCopyFailed), { kind: 'error' });
  }
}

async function templates(ctx: CommandContext): Promise<void> {
  const st = ctx.editor.getState();
  const line = st.focusedColumn?.line ?? st.activeLine;
  if (ctx.store === null || line === null) {
    ctx.ui.toast(ref(m.needActiveLine), { kind: 'warning' });
    return;
  }
  const at = snappedPlayhead(ctx);
  // One tool sheet at a time (UX-038): the generator sheets close (their previews revert) first.
  for (const id of [DIALOG.distribute, DIALOG.clone, DIALOG.fill, DIALOG.motionPath, DIALOG.bake, DIALOG.flatten])
    ctx.ui.closeDialog(id);
  const res = await ctx.ui.openDialog(DIALOG.templates, { line, at });
  if (res !== null && st.selection.lines.size > 1) {
    ctx.ui.toast(ref(m.templatesMulti, { line: ctx.store.doc.lines.get(line)?.name ?? String(line) }), {
      kind: 'info',
    });
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------------------------------------------

/** Asks for a property; the names follow the kinds of `lines` (Length on lines, Size X on images and texts). */
async function chooseProp(
  ctx: CommandContext,
  title: string,
  props: readonly LineProp[],
  lines: readonly LineId[],
): Promise<LineProp | null> {
  const store = ctx.store;
  const ctxs = store === null ? [] : lines.filter((l) => store.doc.lines.has(l)).map((l) => propCtxOf(store, l));
  return ctx.ui.choose<LineProp>({
    title: ref(title),
    options: props.map((p) => ({ value: p, label: ref(propNameAcross(p, ctxs)) })),
  });
}

/** Properties that have a column on a line now (its motion columns at the current level), in column order. */
export function columnPropsOf(ctx: Services, line: LineId): LineProp[] {
  const store = ctx.store;
  if (store === null) return [];
  const specs = lineColumns(
    { doc: store.doc, idx: store.idx, editor: ctx.editor.getState(), level: levelOf(ctx) },
    line,
  );
  return [...new Set(specs.flatMap((c) => c.lanes.map((l) => l.prop)))];
}

/** The line "Focus column" acts on: the active line (its columns are shown), else the focused column's. */
function focusLineOf(ctx: CommandContext): LineId | null {
  const st = ctx.editor.getState();
  const line = st.activeLine ?? st.focusedColumn?.line ?? null;
  return line !== null && ctx.store?.doc.lines.has(line) === true ? line : null;
}

/**
 * "Focus column…": without a property, asks among the line's columns and the properties that can be added (an added
 * one is created first: Add property, with the line type dialog for text, brush and GIF). With a property (palette)
 * that has no column on the line, it is added the same way; one that cannot exist there is refused with a toast.
 * Never creates a track that changes the line type behind the user's back (DESIGN.md §6.13).
 */
async function focusColumn(ctx: CommandContext, args: unknown): Promise<void> {
  const store = ctx.store;
  const explicit = propArg(args);
  const argLine = numArg(args, 'line') as LineId | undefined;
  const line = argLine !== undefined && store?.doc.lines.has(argLine) === true ? argLine : focusLineOf(ctx);
  if (store === null || line === null) {
    ctx.ui.toast(ref(m.needActiveLine), { kind: 'warning' });
    return;
  }
  const shown = columnPropsOf(ctx, line);
  const pc = propCtxOf(store, line);
  const name = (p: LineProp): string => ctx.i18n.t(propNames(p, pc).full);
  let prop: LineProp;
  if (explicit === undefined) {
    const addable = addablePropsOf(store, line, levelOf(ctx)).filter((p) => !shown.includes(p));
    const choice = await ctx.ui.choose<string>({
      title: ref(m.focusPropTitle),
      options: [
        ...shown.map((p) => ({ value: p, label: ref(propNames(p, pc).full) })),
        ...addable.map((p) => ({ value: `add:${p}`, label: ref(m.focusAddOption, { prop: name(p) }) })),
      ],
    });
    if (choice === null) return;
    const picked = choice.startsWith('add:') ? choice.slice(4) : choice;
    const p = propArg(picked);
    if (p === undefined) return;
    prop = p;
  } else prop = explicit;
  let col: ColumnRef | null;
  if (shown.includes(prop) || (store.idx.tracksOfLine(line).get(prop)?.length ?? 0) > 0) {
    col = columnArg(ctx, { ...obj(args), prop, line });
  } else {
    const change = lineTypeChange(store, line, prop);
    if (change === null && !addablePropsOf(store, line, 'full').includes(prop)) {
      ctx.ui.toast(ref(m.focusUnavailable, { prop: name(prop), line: store.doc.lines.get(line)?.name ?? '' }), {
        kind: 'warning',
      });
      return;
    }
    // Text, brush and GIF go through the line type dialog (the column appears once the line has that type).
    if (!(await addProperty(ctx, line, prop))) return;
    const live = ctx.store;
    if (change !== null && (live === null || (live.idx.tracksOfLine(line).get(prop)?.length ?? 0) === 0)) return;
    col = { line, prop, layer: 0 };
  }
  if (col === null) {
    ctx.ui.toast(ref(m.needActiveLine), { kind: 'warning' });
    return;
  }
  const st = ctx.editor.getState();
  st.setFocusedColumn(col);
  if (!st.timeline.showMotionColumns) st.setTimeline({ showMotionColumns: true });
  const s = session(ctx);
  s.relayout();
  const found = findLane(s.specs, col);
  const id = found?.column.id ?? columnIdOf(col);
  const host = ctx.timeline;
  const ctl = host === null ? null : asTimelineHostController(host);
  ctl?.revealColumn(id);
  // Keyboard focus moves there too (focus.motion: K, Shift+, / Shift+. and the other column keys apply).
  const el = s.columnElementOf(id);
  if (el !== null && typeof el.focus === 'function') {
    try {
      el.focus({ preventScroll: true });
    } catch {
      // a detached element cannot take focus; the focused column is still set
    }
  }
}

async function compare(ctx: CommandContext, args: unknown): Promise<void> {
  const lines = compareCandidates(ctx);
  if (lines.length < 2) {
    ctx.ui.toast(ref(m.needTwoLines), { kind: 'warning' });
    return;
  }
  let prop = propArg(args) ?? ctx.editor.getState().focusedColumn?.prop ?? null;
  prop ??= await chooseProp(
    ctx,
    m.comparePropTitle,
    ['moveX', 'moveY', 'rotate', 'alpha', 'speed', 'scaleX', 'scaleY', 'color'],
    lines,
  );
  if (prop === null) return;
  compareLines(ctx, prop, lines);
  session(ctx).relayout();
}

// ---------------------------------------------------------------------------------------------------------------
// Command list
// ---------------------------------------------------------------------------------------------------------------

/** Palette arguments name properties without a line in view: "Length / Size X" where the name depends on it. */
const PROP_OPTIONS = PROPS.map((p) => ({ value: p, label: ref(neutralPropName(p)) }));

export const MOTION_COMMANDS: readonly CommandDef[] = [
  // ---- keys ----
  {
    id: 'keys.addAtPlayhead',
    title: m.cmdAddKey,
    category: 'Motion',
    icon: 'DiamondPlus',
    aliases: ['keyframe', 'add key', 'insert key', 'split', '关键帧'],
    level: 'guided',
    hint: 60,
    enabled: projectOpen,
    menus: [ctxMenu('keys', 10)],
    async run(ctx) {
      await addKeyAtPlayhead(ctx);
    },
  },
  {
    id: 'keys.addAll',
    title: m.cmdAddKeyAll,
    category: 'Motion',
    icon: 'Diamond',
    aliases: ['key all', 'keyframe everything'],
    level: 'guided',
    hint: 25,
    enabled: projectOpen,
    menus: [ctxMenu('keys', 20)],
    async run(ctx) {
      await addKeysAll(ctx);
    },
  },
  {
    id: 'keys.addHere',
    title: m.cmdAddKeyHere,
    category: 'Motion',
    icon: 'DiamondPlus',
    level: 'guided',
    enabled: projectOpen,
    menus: [ctxMenu('keys', 5)],
    run(ctx) {
      addKeyHere(ctx);
    },
  },
  {
    id: 'keys.delete',
    title: m.cmdDeleteKeys,
    category: 'Motion',
    icon: 'Trash2',
    aliases: ['remove keyframe', 'delete key'],
    level: 'guided',
    hint: 30,
    enabled: (ctx) => {
      const store = ctx.store;
      if (store === null) return ref(m.needProject);
      return selectedKeys(store, ctx.editor.getState().selection).length > 0 ? true : ref(m.needKeys);
    },
    menus: [ctxMenu('keys', 30)],
    async run(ctx) {
      await deleteSelectedKeys(ctx);
    },
  },
  {
    id: 'segments.delete',
    title: m.cmdDeleteSegments,
    category: 'Motion',
    icon: 'Trash2',
    aliases: ['delete event', 'remove segment', 'delete segment', 'EVT-07', '删除事件'],
    level: 'guided',
    hint: 30,
    hintSentence: m.cmdDeleteSegmentsHint,
    enabled: (ctx) => {
      const store = ctx.store;
      if (store === null) return ref(m.needProject);
      return selectedSegmentStarts(store, ctx.editor.getState().selection).length > 0 ? true : ref(m.needSegments);
    },
    menus: [ctxMenu('segment', 60)],
    async run(ctx) {
      await deleteSelectedSegments(ctx);
    },
  },
  {
    id: 'segments.selectKeys',
    title: m.cmdSelectSegmentKeys,
    category: 'Select',
    icon: 'Diamond',
    level: 'guided',
    enabled: hasSegments,
    run(ctx) {
      selectSegmentKeys(ctx);
    },
  },
  {
    id: 'keys.valueUp',
    title: m.cmdNudgeValueUp,
    category: 'Motion',
    icon: 'ArrowRight',
    level: 'guided',
    repeat: true,
    hint: 15,
    enabled: hasKeys,
    async run(ctx, args) {
      await nudgeValues(ctx, 1, numArg(args, 'times') ?? 1);
    },
  },
  {
    id: 'keys.valueDown',
    title: m.cmdNudgeValueDown,
    category: 'Motion',
    icon: 'ArrowLeft',
    level: 'guided',
    repeat: true,
    enabled: hasKeys,
    async run(ctx, args) {
      await nudgeValues(ctx, -1, numArg(args, 'times') ?? 1);
    },
  },
  {
    id: 'keys.later',
    title: m.cmdNudgeTimeLater,
    category: 'Motion',
    icon: 'ArrowUp',
    level: 'guided',
    repeat: true,
    enabled: hasKeys,
    async run(ctx, args) {
      await nudgeTime(ctx, 1, numArg(args, 'times') ?? 1);
    },
  },
  {
    id: 'keys.earlier',
    title: m.cmdNudgeTimeEarlier,
    category: 'Motion',
    icon: 'ArrowDown',
    level: 'guided',
    repeat: true,
    enabled: hasKeys,
    async run(ctx, args) {
      await nudgeTime(ctx, -1, numArg(args, 'times') ?? 1);
    },
  },
  {
    id: 'keys.selectColumn',
    title: m.cmdSelectColumn,
    category: 'Select',
    icon: 'Rows3',
    level: 'guided',
    enabled: projectOpen,
    menus: [ctxMenu('column', 10)],
    run(ctx, args) {
      if (targetColumn(ctx, args) !== null) selectColumn(ctx, args);
    },
  },
  {
    id: 'keys.resetProperty',
    title: m.cmdResetProperty,
    category: 'Motion',
    icon: 'Undo2',
    aliases: ['remove motion', 'clear property', 'reset'],
    level: 'guided',
    enabled: projectOpen,
    menus: [ctxMenu('column', 90)],
    async run(ctx, args) {
      if (targetColumn(ctx, args) !== null) await resetProperty(ctx, args);
    },
  },
  // ---- segments ----
  {
    id: 'segments.insert',
    title: m.cmdSplitAtPlayhead,
    category: 'Motion',
    icon: 'SquareSplitVertical',
    aliases: ['split at playhead', 'cut here', 'insert key'],
    level: 'guided',
    enabled: projectOpen,
    menus: [ctxMenu('keys', 15)],
    async run(ctx) {
      await splitAtPlayhead(ctx);
    },
  },
  {
    id: 'segments.easing',
    title: m.cmdEasing,
    category: 'Motion',
    icon: 'Spline',
    aliases: ['easing', 'ease', 'curve', 'how it moves', '缓动'],
    level: 'guided',
    hint: 55,
    // Without a segment the reason says what to do; a focused column without two keyframes says to add them first
    // (C over a fresh line, UX-091).
    enabled: (ctx) => {
      const ok = hasSegments(ctx);
      if (ok === true || ctx.store === null) return ok;
      const col = ctx.editor.getState().focusedColumn;
      const track = col === null ? undefined : ctx.store.idx.track(col.line, col.prop, col.layer);
      const keys = track === undefined ? 0 : ctx.store.idx.keysByTrack(track).length;
      return col !== null && keys < 2 ? ref(m.needTwoKeys) : ok;
    },
    menus: [ctxMenu('segment', 10)],
    async run(ctx) {
      const store = ctx.store;
      if (store === null) return;
      await openEasingPicker(ctx, session(ctx), segmentsOrKeys(store, ctx.editor.getState().selection));
    },
  },
  {
    id: 'segments.hold',
    title: m.cmdHold,
    category: 'Motion',
    icon: 'StepForward',
    aliases: ['stay', 'lock', 'hold', 'constant', '钩定'],
    level: 'guided',
    hint: 50,
    enabled: hasSegments,
    menus: [ctxMenu('segment', 20)],
    async run(ctx) {
      await toggleHold(ctx);
    },
  },
  {
    id: 'segments.jump',
    title: m.cmdJump,
    category: 'Motion',
    icon: 'ChevronsUpDown',
    aliases: ['jump', 'stick', 'continue from previous', '粘合'],
    level: 'guided',
    hint: 45,
    enabled: (ctx, args) => {
      const store = ctx.store;
      if (store !== null && (keysArg(store, args)?.length ?? 0) > 0) return true;
      return hasKeys(ctx);
    },
    menus: [ctxMenu('segment', 30)],
    async run(ctx, args) {
      await toggleJump(ctx, args);
    },
  },
  {
    id: 'segments.negate',
    title: m.cmdNegate,
    category: 'Motion',
    icon: 'FlipVertical2',
    aliases: ['negate', 'invert value', 'opacity 0'],
    level: 'guided',
    hint: 40,
    enabled: hasKeys,
    menus: [ctxMenu('segment', 40)],
    async run(ctx) {
      await negate(ctx);
    },
  },
  {
    id: 'segments.opacity',
    title: m.cmdOpacityFull,
    category: 'Motion',
    icon: 'Eye',
    aliases: ['opacity 255', 'show', 'full opacity'],
    level: 'guided',
    hint: 35,
    enabled: (ctx) => {
      const store = ctx.store;
      if (store === null) return ref(m.needProject);
      return opacityKeys(store, keysOrSegments(store, ctx.editor.getState().selection)).length > 0
        ? true
        : ref(m.needOpacityKeys);
    },
    menus: [ctxMenu('segment', 50)],
    async run(ctx) {
      await opacityFull(ctx);
    },
  },
  // ---- layers ----
  {
    id: 'layers.add',
    title: m.cmdLayerAdd,
    category: 'Motion',
    icon: 'Layers',
    aliases: ['new layer', 'event layer', '事件层级'],
    level: 'full',
    enabled: layeredFocus,
    menus: [ctxMenu('layers', 10)],
    run(ctx, args) {
      const col = targetColumn(ctx, args, true);
      if (col !== null) addLayer(ctx, col.line, col.prop);
    },
  },
  {
    id: 'layers.delete',
    title: m.cmdLayerDelete,
    category: 'Motion',
    icon: 'Trash2',
    level: 'full',
    enabled: layeredFocus,
    async run(ctx, args) {
      const col = targetColumn(ctx, args, true);
      if (col !== null) await deleteLayer(ctx, col);
    },
  },
  {
    id: 'layers.rename',
    title: m.cmdLayerRename,
    category: 'Motion',
    icon: 'Pencil',
    level: 'full',
    args: { kind: 'text', label: ref(m.argName) },
    enabled: layeredFocus,
    run(ctx, args) {
      const name = strArg(args, 'name') ?? (typeof args === 'string' ? args : undefined);
      const col = targetColumn(ctx, typeof args === 'string' ? undefined : args, true);
      if (col !== null && name !== undefined) renameLayer(ctx, col.line, col.layer, name);
    },
  },
  {
    id: 'layers.setActive',
    title: m.cmdLayerSetActive,
    category: 'Motion',
    icon: 'Layers',
    level: 'full',
    args: { kind: 'number', label: ref(m.argLayer), min: 0, max: 255 },
    enabled: projectOpen,
    run(ctx, args) {
      const layer = numArg(args, 'layer');
      if (layer !== undefined) setActiveLayer(ctx, layer);
    },
  },
  {
    id: 'layers.moveSelection',
    title: m.cmdLayerMoveSelection,
    category: 'Motion',
    icon: 'Layers',
    level: 'full',
    args: { kind: 'number', label: ref(m.argLayer), min: 0, max: 255 },
    enabled: hasSegments,
    menus: [ctxMenu('layers', 20)],
    run(ctx, args) {
      const layer = numArg(args, 'layer');
      if (layer !== undefined) moveSelectionToLayer(ctx, layer);
    },
  },
  {
    id: 'layers.mute',
    title: m.cmdLayerMute,
    category: 'Motion',
    icon: 'VolumeX',
    aliases: ['mute layer', 'hide layer'],
    level: 'full',
    enabled: layeredFocus,
    checked: (ctx) => {
      const col = ctx.editor.getState().focusedColumn;
      return col !== null && isMuted(ctx, col.line, col.prop, col.layer);
    },
    menus: [ctxMenu('layers', 30)],
    run(ctx, args) {
      const col = targetColumn(ctx, args, true);
      if (col !== null) toggleMute(ctx, col);
    },
  },
  {
    id: 'layers.solo',
    title: m.cmdLayerSolo,
    category: 'Motion',
    icon: 'Headphones',
    level: 'full',
    enabled: layeredFocus,
    checked: (ctx) => {
      const col = ctx.editor.getState().focusedColumn;
      return col !== null && isSolo(ctx, col);
    },
    menus: [ctxMenu('layers', 40)],
    run(ctx, args) {
      const col = targetColumn(ctx, args, true);
      if (col !== null) toggleSolo(ctx, col);
    },
  },
  {
    id: 'layers.unmuteAll',
    title: m.cmdLayerUnmuteAll,
    category: 'Motion',
    icon: 'Volume2',
    level: 'full',
    enabled: (ctx) => (ctx.editor.getState().filters.mutedLayers.size > 0 ? true : ref(m.needMuted)),
    run(ctx) {
      unmuteAll(ctx);
    },
  },
  // ---- motion ----
  {
    id: 'motion.toggleLink',
    title: m.cmdToggleLink,
    category: 'Motion',
    icon: 'Link',
    aliases: ['xy bind', 'link x y', 'MoveX&Y Binded'],
    level: 'guided',
    enabled: (ctx) =>
      ctx.store !== null && (ctx.editor.getState().focusedColumn?.line ?? ctx.editor.getState().activeLine) !== null
        ? true
        : ref(m.needXyLine),
    checked: (ctx) => {
      const line = ctx.editor.getState().focusedColumn?.line ?? ctx.editor.getState().activeLine;
      return line !== null && ctx.store?.doc.lines.get(line)?.editor.xyLinked === true;
    },
    menus: [ctxMenu('column', 30)],
    run(ctx, args) {
      const line =
        (numArg(args, 'line') as LineId | undefined) ??
        ctx.editor.getState().focusedColumn?.line ??
        ctx.editor.getState().activeLine;
      if (line !== null) toggleLink(ctx, line);
    },
  },
  {
    id: 'motion.addProperty',
    title: m.cmdAddProperty,
    category: 'Motion',
    icon: 'ListPlus',
    aliases: ['add property', 'colour', 'size', 'length', 'thickness', 'text', 'tilt'],
    level: 'guided',
    args: { kind: 'choice', label: ref(m.argProp), options: PROP_OPTIONS },
    enabled: (ctx) => (ctx.store !== null && ctx.editor.getState().activeLine !== null ? true : ref(m.needActiveLine)),
    menus: [ctxMenu('column', 20)],
    async run(ctx, args) {
      const store = ctx.store;
      const line = (numArg(args, 'line') as LineId | undefined) ?? ctx.editor.getState().activeLine;
      if (store === null || line === null) return;
      let prop = propArg(args) ?? null;
      if (prop === null) {
        const name = store.doc.lines.get(line)?.name ?? String(line);
        const list = addablePropsOf(store, line, levelOf(ctx));
        const pc = propCtxOf(store, line);
        prop = await ctx.ui.choose<LineProp>({
          title: ref(m.addPropertyTitle),
          body: ref(m.addPropertyBody, { line: name }),
          options: list.map((p) => ({ value: p, label: ref(propNames(p, pc).full) })),
        });
      }
      if (prop !== null) await addProperty(ctx, line, prop);
    },
  },
  {
    id: 'motion.toggleSigma',
    title: m.cmdToggleSigma,
    category: 'View',
    icon: 'Sigma',
    level: 'full',
    enabled: layeredFocus,
    checked: (ctx) => {
      const col = ctx.editor.getState().focusedColumn;
      return col !== null && !session(ctx).sigmaHidden.has(`${String(col.line)}:${col.prop}`);
    },
    menus: [ctxMenu('view', 10)],
    run(ctx) {
      const col = ctx.editor.getState().focusedColumn;
      if (col === null) return;
      const s = session(ctx);
      const k = `${String(col.line)}:${col.prop}`;
      if (s.sigmaHidden.has(k)) s.sigmaHidden.delete(k);
      else s.sigmaHidden.add(k);
      s.touch();
      s.invalidate('content');
    },
  },
  {
    id: 'motion.lockRange',
    title: m.cmdLockRange,
    category: 'View',
    icon: 'Lock',
    level: 'guided',
    enabled: hasFocus,
    checked: (ctx) => {
      const col = ctx.editor.getState().focusedColumn;
      return col !== null && session(ctx).lockedRanges.has(laneKeyOf(col.line, col.prop, col.layer));
    },
    menus: [ctxMenu('view', 20)],
    run(ctx) {
      const col = ctx.editor.getState().focusedColumn;
      if (col === null) return;
      const s = session(ctx);
      const k = laneKeyOf(col.line, col.prop, col.layer);
      if (s.lockedRanges.has(k)) s.lockedRanges.delete(k);
      else {
        const r = s.lastRanges.get(k);
        if (r !== undefined) s.lockedRanges.set(k, r);
      }
      s.touch();
      s.invalidate('content');
    },
  },
  {
    id: 'motion.toggleRaw',
    title: m.cmdShowRaw,
    category: 'View',
    icon: 'Braces',
    aliases: ['raw events', 'rpe events', 'json'],
    level: 'full',
    enabled: projectOpen,
    checked: (ctx) => {
      const col = ctx.editor.getState().focusedColumn;
      return col !== null && session(ctx).rawShown.has(laneKeyOf(col.line, col.prop, col.layer));
    },
    menus: [ctxMenu('view', 30)],
    run(ctx, args) {
      const col = targetColumn(ctx, args);
      if (col === null) return;
      const s = session(ctx);
      const k = laneKeyOf(col.line, col.prop, col.layer);
      if (s.rawShown.has(k)) s.rawShown.delete(k);
      else s.rawShown.add(k);
      s.touch();
      s.invalidate('content');
    },
  },
  {
    id: 'motion.copyRaw',
    title: m.cmdCopyRaw,
    category: 'Edit',
    icon: 'Copy',
    level: 'full',
    enabled: projectOpen,
    menus: [ctxMenu('view', 40)],
    async run(ctx, args) {
      if (targetColumn(ctx, args) !== null) await copyRaw(ctx, args);
    },
  },
  {
    id: 'motion.copyCurve',
    title: m.cmdCopyCurve,
    category: 'Edit',
    icon: 'Copy',
    level: 'guided',
    enabled: hasSegments,
    menus: [ctxMenu('segment', 60)],
    run(ctx) {
      copyCurve(ctx);
    },
  },
  {
    id: 'motion.pasteCurve',
    title: m.cmdPasteCurve,
    category: 'Edit',
    icon: 'ClipboardPaste',
    level: 'guided',
    enabled: (ctx) => {
      const r = hasSegments(ctx);
      if (r !== true) return r;
      return session(ctx).copiedCurve !== null ? true : ref(m.needCurve);
    },
    menus: [ctxMenu('segment', 70)],
    async run(ctx) {
      await pasteCurve(ctx);
    },
  },
  {
    id: 'motion.templates',
    title: m.cmdTemplates,
    category: 'Motion',
    icon: 'WandSparkles',
    aliases: ['templates', 'motion template', 'preset'],
    level: 'guided',
    enabled: (ctx) =>
      ctx.store !== null && (ctx.editor.getState().focusedColumn ?? ctx.editor.getState().activeLine) !== null
        ? true
        : ref(m.needActiveLine),
    menus: [ctxMenu('keys', 40)],
    async run(ctx) {
      await templates(ctx);
    },
  },
  {
    id: 'motion.clearPinned',
    title: m.cmdClearPinned,
    category: 'View',
    icon: 'PinOff',
    level: 'full',
    enabled: (ctx) => (ctx.editor.getState().pinnedColumns.length > 0 ? true : ref(m.needPinned)),
    menus: [ctxMenu('column', 60)],
    run(ctx) {
      clearPinned(ctx);
    },
  },
  {
    id: 'motion.placeSegment',
    title: m.cmdPlaceSegment,
    category: 'Motion',
    icon: 'Spline',
    aliases: ['place event', 'R R'],
    level: 'guided',
    hint: 20,
    enabled: projectOpen,
    async run(ctx, args) {
      await placementPress(session(ctx), obj(args).constant === true);
    },
  },
  {
    id: 'motion.placeHold',
    title: m.cmdPlaceHold,
    category: 'Motion',
    icon: 'StepForward',
    aliases: ['place stay', 'lock event', 'Q R'],
    level: 'guided',
    enabled: projectOpen,
    async run(ctx) {
      await placementPress(session(ctx), true);
    },
  },
  {
    id: 'motion.placeDigit',
    title: m.cmdPlaceDigit,
    category: 'Motion',
    icon: 'Spline',
    level: 'guided',
    args: { kind: 'number', label: ref(m.argDigit), min: 0, max: 9 },
    enabled: (ctx) => (ctx.editor.getState().placement?.kind === 'segment' ? true : ref(m.needPlacement)),
    run(ctx, args) {
      const d = numArg(args, 'digit');
      if (d !== undefined) placementDigit(session(ctx), d);
    },
  },
  {
    id: 'motion.cancelPlacement',
    title: m.cmdPlaceCancel,
    category: 'Motion',
    icon: 'X',
    level: 'guided',
    enabled: (ctx) => (ctx.editor.getState().placement?.kind === 'segment' ? true : ref(m.needPlacement)),
    run(ctx) {
      cancelPlacement(session(ctx));
    },
  },
  {
    id: 'motion.fineTune',
    title: m.cmdFineTune,
    category: 'Motion',
    icon: 'ArrowUpDown',
    level: 'guided',
    args: { kind: 'number', label: ref(m.argSteps), min: -100, max: 100 },
    enabled: hasKeys,
    async run(ctx, args) {
      const store = ctx.store;
      const steps = numArg(args, 'steps') ?? 1;
      if (store === null) return;
      const s = session(ctx);
      if (!(await confirmGuidedKeys(ctx, s, keysOrSegments(store, ctx.editor.getState().selection)))) return;
      fineTune(s, keysOrSegments(store, ctx.editor.getState().selection), steps);
    },
  },
  {
    id: 'motion.switchToFull',
    title: m.guidedLayersFull,
    category: 'View',
    icon: 'Layers',
    level: 'guided',
    enabled: (ctx) => (levelOf(ctx) === 'guided' ? true : ref(m.needFullForLayers)),
    run(ctx) {
      switchToFull(ctx);
    },
  },
  // ---- columns ----
  {
    id: 'columns.focus',
    title: m.cmdFocusColumn,
    category: 'Motion',
    icon: 'Crosshair',
    aliases: ['focus column', 'select column'],
    level: 'guided',
    args: { kind: 'choice', label: ref(m.argProp), options: PROP_OPTIONS },
    enabled: (ctx) => (ctx.store !== null && ctx.editor.getState().activeLine !== null ? true : ref(m.needActiveLine)),
    async run(ctx, args) {
      await focusColumn(ctx, args);
    },
  },
  {
    id: 'columns.pin',
    title: m.cmdPinColumn,
    category: 'View',
    icon: 'Pin',
    aliases: ['pin column', 'unpin column', 'keep column'],
    level: 'full',
    enabled: projectOpen,
    checked: (ctx) => {
      const col = ctx.editor.getState().focusedColumn;
      return col !== null && isPinned(ctx, col);
    },
    menus: [ctxMenu('column', 40)],
    run(ctx, args) {
      const col = targetColumn(ctx, args);
      if (col === null) return;
      togglePin(ctx, col);
      session(ctx).relayout();
    },
  },
  {
    id: 'columns.compare',
    title: m.cmdCompare,
    category: 'View',
    icon: 'GitCompareArrows',
    aliases: ['compare lines', 'side by side'],
    level: 'full',
    args: { kind: 'choice', label: ref(m.argProp), options: PROP_OPTIONS },
    enabled: (ctx) => (compareCandidates(ctx).length >= 2 ? true : ref(m.needTwoLines)),
    menus: [ctxMenu('column', 50), { menu: 'lines.context', group: 'compare', order: 10 }],
    async run(ctx, args) {
      await compare(ctx, args);
    },
  },
];

// ---------------------------------------------------------------------------------------------------------------
// Key bindings (DESIGN.md §8.2 Default, §8.3 RPE)
// ---------------------------------------------------------------------------------------------------------------

const kb = (chord: string, command: string, extra: Partial<KeyBinding> = {}): KeyBinding => ({
  chord,
  command,
  ...extra,
});

/** Keyframes or segments selected and no notes (the note lane binds the same keys for notes). */
const KEYS_SEL = '(sel.keys || sel.segments) && !sel.notes';
const SEG_SEL = 'sel.segments && !sel.notes && !paste.ghost';

const SHARED: readonly KeyBinding[] = [
  kb('KeyK', 'keys.addAtPlayhead', { when: '!textInput' }),
  kb('Shift+KeyK', 'keys.addAll', { when: '!textInput' }),
  kb('Delete', 'keys.delete', { when: 'sel.keys && !sel.notes' }),
  kb('Backspace', 'keys.delete', { when: 'sel.keys && !sel.notes' }),
  // Segments only (no keys, no notes): delete the segments (UX-037).
  kb('Delete', 'segments.delete', { when: 'sel.segments && !sel.keys && !sel.notes && !textInput' }),
  kb('Backspace', 'segments.delete', { when: 'sel.segments && !sel.keys && !sel.notes && !textInput' }),
  kb('ArrowRight', 'keys.valueUp', { when: KEYS_SEL }),
  kb('ArrowLeft', 'keys.valueDown', { when: KEYS_SEL }),
  kb('Shift+ArrowRight', 'keys.valueUp', { when: KEYS_SEL, args: { times: 10 } }),
  kb('Shift+ArrowLeft', 'keys.valueDown', { when: KEYS_SEL, args: { times: 10 } }),
  kb('ArrowUp', 'keys.later', { when: KEYS_SEL }),
  kb('ArrowDown', 'keys.earlier', { when: KEYS_SEL }),
  kb('Shift+ArrowUp', 'keys.later', { when: KEYS_SEL, args: { times: 4 } }),
  kb('Shift+ArrowDown', 'keys.earlier', { when: KEYS_SEL, args: { times: 4 } }),
  kb('Enter', 'segments.easing', { when: SEG_SEL }),
  kb('KeyC', 'segments.easing', { when: SEG_SEL }),
  // C on a focused column with nothing selected explains how to get a segment (the reason toast), never nothing.
  kb('KeyC', 'segments.easing', { when: 'focus.motion && !sel.segments && !sel.keys && !sel.notes && !textInput' }),
  kb('KeyH', 'segments.hold', { when: KEYS_SEL }),
  kb('KeyJ', 'segments.jump', { when: KEYS_SEL }),
  kb('KeyA', 'segments.negate', { when: KEYS_SEL }),
  kb('KeyS', 'segments.opacity', { when: KEYS_SEL }),
];

/** RPE preset: R … R / Q … R segment placement over a motion column, digits for the curve, Esc cancels. */
const RPE_ONLY: readonly KeyBinding[] = [
  kb('KeyR', 'motion.placeSegment', { when: '(hover.motion || placing.segment) && !textInput' }),
  kb('KeyQ', 'motion.placeHold', { when: 'hover.motion && !placing.segment && !textInput' }),
  ...[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) =>
    kb(`Digit${String(d)}`, 'motion.placeDigit', { when: 'placing.segment && !textInput', args: { digit: d } }),
  ),
  kb('Escape', 'motion.cancelPlacement', { when: 'placing.segment && !textInput' }),
];

export const MOTION_KEYBINDINGS: Readonly<Record<KeymapPresetId, readonly KeyBinding[]>> = {
  default: SHARED,
  rpe: [...SHARED, ...RPE_ONLY],
};
